/**
 * QuotationsListPage — table view of quotations with status tabs, search, and pagination.
 *
 * Features:
 * - Status tabs: Todas, Borrador, Enviada, Aceptada, Rechazada
 * - Text search by client name or contact email
 * - Pagination (limit/offset)
 * - Click row to navigate to detail
 * - Role-based visibility handled server-side
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuotations } from '@/hooks/useQuotations'
import { QuotationStatusBadge } from '@/components/quotations/QuotationStatusActions'
import type { QuotationStatus, QuotationListFilters } from '@1klick/types'

const STATUS_TABS: { label: string; value: QuotationStatus | undefined }[] = [
  { label: 'Todas', value: undefined },
  { label: 'Borrador', value: 'draft' },
  { label: 'Enviadas', value: 'sent' },
  { label: 'Aceptadas', value: 'accepted' },
  { label: 'Rechazadas', value: 'rejected' },
]

const PAGE_SIZE = 25

function formatCents(cents: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(cents / 100)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function QuotationsListPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<QuotationStatus | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const filters: QuotationListFilters = {
    status: activeTab,
    search: search.trim() || undefined,
    limit: PAGE_SIZE,
    offset,
  }

  const { data: quotations, isLoading, isError } = useQuotations(filters)

  const handleTabChange = (status: QuotationStatus | undefined) => {
    setActiveTab(status)
    setOffset(0)
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Cotizaciones</h1>
        <Link
          to="/quotations/new"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Nueva cotizacion
        </Link>
      </div>

      {/* Search + Status tabs */}
      <div className="mb-4 space-y-4">
        {/* Search */}
        <div>
          <input
            type="text"
            placeholder="Buscar por nombre de cliente o email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setOffset(0)
            }}
            className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => handleTabChange(tab.value)}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === tab.value
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">Cargando cotizaciones...</p>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-500">Error al cargar las cotizaciones</p>
          </div>
        )}

        {!isLoading && !isError && quotations && (
          <>
            {quotations.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <p className="text-gray-500">No hay cotizaciones</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Cliente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Contacto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Creada por
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Fecha
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {quotations.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/quotations/${q.id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        {q.client_name}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {q.contact_email}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <QuotationStatusBadge status={q.status} />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                        {formatCents(q.total_cents)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {q.creator_name ?? '-'}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatDate(q.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Mostrando {offset + 1} - {offset + (quotations?.length ?? 0)}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={(quotations?.length ?? 0) < PAGE_SIZE}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
